import React, { useState, useEffect } from 'react';
import { FluentProvider, webLightTheme, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { Header } from './components/Header';
import { StartingBalanceBar } from './components/StartingBalanceBar';
import { useRegisterTable } from './components/RegisterTable';
import { InsightsPanel } from './components/InsightsPanel';
import { Footer } from './components/Footer';
import { LoginPage } from './components/LoginPage';
import { GuidedTour } from './components/GuidedTour';
import { useAccounts } from './hooks/useAccounts';
import { useTransactions } from './hooks/useTransactions';
import { useIsMobile } from './hooks/useIsMobile';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    '@media (max-width: 767px)': {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      paddingTop: tokens.spacingVerticalS,
      paddingBottom: '80px',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '100px',
  },
});

function AuthenticatedApp() {
  const styles = useStyles();
  const [view, setView] = useState<'register' | 'insights'>('register');
  const isMobile = useIsMobile();
  const { accounts: msalAccounts } = useMsal();
  const idTokenClaims = (msalAccounts[0] as any)?.idTokenClaims;
  const roles: string[] = idTokenClaims?.roles || [];
  const isAdmin = roles.includes('Admin');

  const {
    accounts, selectedAccount, selectedAccountId, setSelectedAccountId,
    loading: accountsLoading, createAccount, updateAccount,
  } = useAccounts();

  const {
    transactions, loading: txnLoading,
    addTransaction, updateTransaction, toggleCleared, deleteTransaction, importTransactions,
  } = useTransactions(selectedAccountId);

  const startingBalance = selectedAccount?.starting_balance || 0;

  // Size the register's scroll area to fill the viewport below the toolbar, so
  // the toolbar + column headers stay frozen while only the rows scroll. We
  // measure the container's distance from the top of the document (which equals
  // the toolbar/chrome height above it) and leave room for the footer. Desktop
  // only; on mobile the register renders as cards without `.register-container`.
  useEffect(() => {
    const getEl = () =>
      document.querySelector('.register-container') as HTMLElement | null;
    // On mobile (cards) or the insights view, let CSS govern: clear any inline
    // height left over from a desktop layout (inline styles beat media queries).
    if (isMobile || view !== 'register') {
      const c = getEl();
      if (c) c.style.maxHeight = '';
      return;
    }
    const sizeRegister = () => {
      const el = getEl();
      if (!el) return;
      const aboveHeight = el.getBoundingClientRect().top + window.scrollY;
      const footerAllowance = 84; // footer + breathing room
      el.style.maxHeight = `calc(100vh - ${Math.round(aboveHeight + footerAllowance)}px)`;
    };
    sizeRegister();
    // Re-measure after layout settles (fonts, wrapping toolbar) and on resize.
    const raf = requestAnimationFrame(sizeRegister);
    window.addEventListener('resize', sizeRegister);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', sizeRegister);
    };
  }, [isMobile, view, accountsLoading, txnLoading, selectedAccountId]);

  const { currentBalance, clearedBalance, unclearedBalance, table } = useRegisterTable({
    transactions,
    startingBalance,
    onAdd: addTransaction,
    onUpdate: updateTransaction,
    onToggleCleared: toggleCleared,
    onDelete: deleteTransaction,
    accountId: selectedAccountId,
  });

  if (accountsLoading || txnLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading BalanceScript..." size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <GuidedTour ready={!accountsLoading && !txnLoading} />
      <Header
        accounts={accounts}
        selectedAccountId={selectedAccountId}
        onSelectAccount={setSelectedAccountId}
        onCreateAccount={(name) => createAccount(name)}
        onRenameAccount={(id, name) => updateAccount(id, { name })}
        currentBalance={currentBalance}
        startingBalance={startingBalance}
        onUpdateStartingBalance={(bal) => {
          if (selectedAccountId) updateAccount(selectedAccountId, { starting_balance: bal });
        }}
        onImported={(rows) => importTransactions(rows)}
        view={view}
        onToggleView={() => setView(v => v === 'register' ? 'insights' : 'register')}
        isAdmin={isAdmin}
      />
      {view === 'register' ? (
        <>
          {isMobile && (
            <StartingBalanceBar
              currentValue={startingBalance}
              onUpdate={(bal) => {
                if (selectedAccountId) updateAccount(selectedAccountId, { starting_balance: bal });
              }}
            />
          )}
          {table}
          <Footer
            txnCount={transactions.length}
            clearedBalance={clearedBalance}
            unclearedBalance={unclearedBalance}
          />
        </>
      ) : (
        <InsightsPanel accountId={selectedAccountId} />
      )}
    </div>
  );
}

function App() {
  return (
    <FluentProvider theme={webLightTheme}>
      <AuthenticatedTemplate>
        <AuthenticatedApp />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </FluentProvider>
  );
}

export default App;
